import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation, useParams } from 'react-router-dom';

import { extraQueryParams } from '@utils/constants';
import { CheckCircle } from '../../../components/Icons';
import { useSessionFeatureState } from '../../../hooks/storeHooks/useSessionState';

interface OutOfMinutesModalProps {
  isSessionPage?: boolean;
  question?: string;
  onClose: () => void;
}

const OutOfMinutesModal: React.FC<OutOfMinutesModalProps> = memo(
  ({ question, isSessionPage, onClose }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { slug } = useParams();
    const { isAuthenticated, signinRedirect } = useAuth();
    const { companionData } = useSessionFeatureState();

    const nonAuthItems = [
      t('outOfMinutes.nonAuthItem1'),
      t('outOfMinutes.nonAuthItem2'),
      t('outOfMinutes.nonAuthItem3'),
    ];

    const authItems = [
      t('outOfMinutes.authItem1'),
      t('outOfMinutes.authItem2'),
      t('outOfMinutes.authItem3'),
      t('outOfMinutes.authItem4'),
    ];

    const getReturnUrl = () => {
      if (question) {
        return `/search?query=${encodeURIComponent(question)}`;
      }
      if (slug) {
        return `${location.pathname}${location.search || '?start=true'}`;
      }
      return '/';
    };

    const handleLogin = () => {
      void signinRedirect({
        state: {
          returnUrl: getReturnUrl(),
        },
        extraQueryParams,
      });
    };

    const handleCreateAccount = async () => {
      await signinRedirect({
        state: {
          returnUrl: getReturnUrl(),
        },
        extraQueryParams: {
          action: 'get-started',
          backgroundUrl: companionData?.videoLoopUrl || '',
        },
      });
    };

    const trackPricingClick = () => {
      console.log('pricing clicked');
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998]">
        <div
          className=" tc-mv-blur rounded-regular p-6 max-w-lg w-full mx-4"
          data-testid="out-of-minutes-modal">
          {!isSessionPage && (
            <button
              onClick={onClose}
              className="text-white absolute top-4 right-4 hover:text-white"
              data-testid="out-of-minutes-modal-close-btn"
              id="out-of-minutes-modal-close-btn">
              <i className="fa-solid fa-xmark text-lg" />
            </button>
          )}
          <div className="flex flex-col justify-center items-center mb-4 text-center">
            <img
              alt="napster-logo"
              src="/assets/images/logo_napster_white.svg"
              className="h-[42px]"
            />
            <h2 className="text-white text-xl  mt-4">
              {isAuthenticated
                ? t('outOfMinutes.usedFreeCredits')
                : t('outOfMinutes.keepConversation')}
            </h2>
          </div>
          <p className="text-white mt-4">
            {isAuthenticated
              ? t('outOfMinutes.grabAPlan')
              : t('outOfMinutes.createFreeAccount')}
          </p>
          <ul className="pl-5 text-white mt-4 list-none !p-0">
            {(isAuthenticated ? authItems : nonAuthItems).map((item, index) => (
              <li key={index} className="mt-1 flex gap-2">
                <CheckCircle className="text-white min-w-4 mt-1" />
                {item}
              </li>
            ))}
          </ul>
          {isAuthenticated ? (
            <Link
              to="/pricing"
              className="btn btn-success mx-auto mt-4 w-fit"
              data-testid="out-of-minutes-modal-buy-minutes"
              id="out-of-minutes-modal-buy-minutes"
              onClick={trackPricingClick}>
              {t('outOfMinutes.getMoreCredits')}
            </Link>
          ) : (
            <div className="flex  justify-center gap-4">
              {!isSessionPage && (
                <button
                  className="btn btn-success !bg-blue mt-4 w-fit"
                  data-testid="out-of-minutes-modal-login"
                  id="out-of-minutes-modal-login"
                  onClick={handleLogin}>
                  {t('outOfMinutes.logIn')}
                </button>
              )}
              <button
                className="btn btn-success mt-4 w-fit"
                data-testid="out-of-minutes-modal-create-acc"
                id="out-of-minutes-modal-create-acc"
                onClick={handleCreateAccount}>
                {t('outOfMinutes.createFreeAccountBtn')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export { OutOfMinutesModal };
